import { Fragment, useState } from 'react';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/Email';
import ListItemButton from '@mui/material/ListItemButton';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Switch from '@mui/material/Switch';
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import groupGameTypes from '../../lib/groupGameTypes';

const ADMIN_EMAIL_ADDRESS = 'admin@guessaulenko.com';

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const Menu = (props) => {
	const [ state, setState ] = useState(false);
	const [ expandedGroups, setExpandedGroups ] = useState({});

	const toggleDrawer = (open) => (event) => {
		if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
			return;
		}
		if (open) document.activeElement?.blur();
		setState(open);
	};

	const toggleGroup = (prefix) => (e) => {
		e.stopPropagation();
		setExpandedGroups(prev => ({ ...prev, [prefix]: !prev[prefix] }));
	};

	const list = () => {
		const menuItems = [
			{
				menu: 'main',
				actionType: 'function',
				linkIcon: <HelpOutlineIcon />,
				linkText: 'How to Play',
				linkKey: 'menu-how-to-play',
				onClick: () => { document.activeElement?.blur(); props.setShowHelp(true); }
			},
			{
				menu: 'main',
				actionType: 'function',
				linkIcon: <BarChartIcon />,
				linkText: 'Your Results',
				linkKey: 'menu-your-results',
				onClick: () => { document.activeElement?.blur(); props.setShowResults(true); }
			},
			{
				menu: 'sub',
				actionType: 'url',
				linkIcon: <TwitterIcon />,
				linkText: 'Twitter',
				linkKey: 'menu-twitter',
				url: 'https://twitter.com/guessaulenko',
        externalUrl: true
			},
			{
				menu: 'sub',
				actionType: 'url',
				linkIcon: <EmailIcon />,
				linkText: 'Email',
				linkKey: 'menu-contact',
				url: 'mailto:' + ADMIN_EMAIL_ADDRESS
			}
		];

		const { topLevel, groups } = groupGameTypes(props.availableGameTypes || []);
		const hasGameTypes = topLevel.length > 0 || Object.keys(groups).length > 0;

		return (
			<Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
				{hasGameTypes && (
					<>
						<List subheader={<ListSubheader disableSticky>Game Modes</ListSubheader>}>
							{topLevel.map((gt) => (
								<ListItemButton key={gt.slug} sx={{ cursor: 'pointer' }} onClick={() => { document.activeElement?.blur(); props.onGameTypeChange(null, gt.slug); }}>
									<ListItemIcon>
										{gt.slug === props.activeGameTypeSlug ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
									</ListItemIcon>
									<ListItemText primary={gt.name || capitalize(gt.slug)} />
								</ListItemButton>
							))}
							{Object.entries(groups).map(([prefix, types]) => {
								const isGroupActive = types.some(gt => gt.slug === props.activeGameTypeSlug);
								const isExpanded = !!expandedGroups[prefix];
								return (
									<Fragment key={prefix}>
										<ListItemButton sx={{ cursor: 'pointer' }} onClick={toggleGroup(prefix)}>
											<ListItemIcon>
												{isGroupActive ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
											</ListItemIcon>
											<ListItemText primary={capitalize(prefix)} />
											{isExpanded ? <ExpandLess /> : <ExpandMore />}
										</ListItemButton>
										<Collapse in={isExpanded} timeout="auto" unmountOnExit>
											<List component="div" disablePadding>
												{types.map(gt => (
													<ListItemButton key={gt.slug} sx={{ pl: 4, cursor: 'pointer' }} onClick={() => { document.activeElement?.blur(); props.onGameTypeChange(null, gt.slug); }}>
														<ListItemIcon>
															{gt.slug === props.activeGameTypeSlug ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
														</ListItemIcon>
														<ListItemText primary={gt.name || capitalize(gt.slug)} />
													</ListItemButton>
												))}
											</List>
										</Collapse>
									</Fragment>
								);
							})}
						</List>
						<Divider />
					</>
				)}
				<List subheader={<ListSubheader disableSticky>Info</ListSubheader>}>
					{menuItems.filter((menuItem) => menuItem.menu === 'main').map((menuItem) => (
						<ListItemButton key={menuItem.linkKey} sx={{ cursor: 'pointer' }} onClick={menuItem.onClick}>
							<ListItemIcon>{menuItem.linkIcon && menuItem.linkIcon}</ListItemIcon>
							<ListItemText primary={menuItem.linkText} />
						</ListItemButton>
					))}
				</List>
				<Divider />
				<List subheader={<ListSubheader disableSticky>Settings</ListSubheader>}>
					<ListItem
						sx={{ cursor: 'pointer' }}
						onClick={(e) => { e.stopPropagation(); props.onColourBlindToggle(); }}
					>
						<ListItemIcon><InvertColorsIcon /></ListItemIcon>
						<ListItemText primary="Colour Blind" />
						<Switch
							checked={props.colourBlindMode}
							onChange={props.onColourBlindToggle}
							onClick={(e) => e.stopPropagation()}
							size="small"
						/>
					</ListItem>
				</List>
				<Divider />
				<List subheader={<ListSubheader disableSticky>Contact</ListSubheader>}>
					{menuItems.filter((menuItem) => menuItem.menu === 'sub').map((menuItem) => (
						<ListItemButton key={menuItem.linkKey} component="a" href={menuItem.url} sx={{ color: 'inherit', textDecoration: 'none' }} {...(menuItem.externalUrl ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
							<ListItemIcon>{menuItem.linkIcon && menuItem.linkIcon}</ListItemIcon>
							<ListItemText primary={menuItem.linkText} />
						</ListItemButton>
					))}
				</List>
			</Box>
		);
	};

	return (
		<Fragment>
			<IconButton onClick={toggleDrawer(true)} aria-label="view menu">
				<MenuIcon />
			</IconButton>
			<Drawer anchor="left" open={state} onClose={toggleDrawer(false)} transitionDuration={0}>
				{list()}
			</Drawer>
		</Fragment>
	);
};

export default Menu;
